const asRecords = (userOrUsers) => {
  const records = Array.isArray(userOrUsers) ? userOrUsers : [userOrUsers];
  return records.filter((record) => record && typeof record === "object");
};

const firstText = (values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }
  return "";
};

export const resolveAvatarSource = (userOrUsers, preferredVariant = "sm") => {
  const records = asRecords(userOrUsers);
  const variantOrder =
    preferredVariant === "md" ? ["md", "sm"] : ["sm", "md"];

  for (const variant of variantOrder) {
    const variantSource = firstText(
      records.map((record) => record.profile_picture_variants?.[variant])
    );
    if (variantSource) return variantSource;
  }

  const profilePicture = firstText(
    records.map((record) => record.profile_picture)
  );
  if (profilePicture) return profilePicture;

  const photoURL = firstText(records.map((record) => record.photoURL));
  if (photoURL) return photoURL;

  for (const record of records) {
    const providers = Array.isArray(record.providerData)
      ? record.providerData
      : [];
    const providerPhoto = firstText(
      providers.map((provider) => provider?.photoURL)
    );
    if (providerPhoto) return providerPhoto;
  }

  return null;
};

export const resolveAvatarDisplayName = (userOrUsers) => {
  const records = asRecords(userOrUsers);
  const directName = firstText(
    records.flatMap((record) => [record.displayName, record.name])
  );
  if (directName) return directName;

  for (const record of records) {
    const providers = Array.isArray(record.providerData)
      ? record.providerData
      : [];
    const providerName = firstText(
      providers.map((provider) => provider?.displayName)
    );
    if (providerName) return providerName;
  }

  return "";
};

export const resolveAvatarUid = (userOrUsers) => {
  const records = asRecords(userOrUsers);

  return firstText(
    records.flatMap((record) => [record.uid, record.userUID, record.id])
  );
};

export const formatAvatarUid = (uid) => {
  const normalizedUid = typeof uid === "string" ? uid.trim() : "";
  if (!normalizedUid) return "";
  if (normalizedUid.length <= 8) return normalizedUid;

  return `${normalizedUid.slice(0, 4)}...${normalizedUid.slice(-4)}`;
};

export const formatAvatarDisplayName = (displayName) => {
  const normalizedName =
    typeof displayName === "string" ? displayName.trim() : "";
  if (!normalizedName) return "";

  const names = normalizedName.split(/\s+/).filter(Boolean);
  if (names.length <= 1) return normalizedName;

  return `${names[0]} ${names[names.length - 1]}`;
};

export const resolveAvatarEmail = (userOrUsers) => {
  const records = asRecords(userOrUsers);
  const directEmail = firstText(records.map((record) => record.email));
  if (directEmail) return directEmail;

  for (const record of records) {
    const providers = Array.isArray(record.providerData)
      ? record.providerData
      : [];
    const providerEmail = firstText(
      providers.map((provider) => provider?.email)
    );
    if (providerEmail) return providerEmail;
  }

  return "";
};

export const isAnonymousAvatarUser = (userOrUsers) => {
  const records = asRecords(userOrUsers);
  const explicitState = records.find(
    (record) => typeof record.isAnonymous === "boolean"
  )?.isAnonymous;

  if (typeof explicitState === "boolean") return explicitState;

  const hasKnownIdentity =
    Boolean(resolveAvatarDisplayName(records)) ||
    Boolean(resolveAvatarEmail(records)) ||
    Boolean(resolveAvatarUid(records));

  return !hasKnownIdentity;
};

export const resolveAvatarName = (
  userOrUsers,
  { anonymousLabel = "Anonymous user", unknownLabel = "Unknown user" } = {}
) => {
  if (isAnonymousAvatarUser(userOrUsers)) return anonymousLabel;

  const displayName = resolveAvatarDisplayName(userOrUsers);
  if (displayName) return displayName;

  return formatAvatarUid(resolveAvatarUid(userOrUsers)) || unknownLabel;
};

export const getAvatarInitial = (userOrUsers) => {
  if (isAnonymousAvatarUser(userOrUsers)) return "A";

  const displayName = resolveAvatarDisplayName(userOrUsers);
  if (displayName) {
    const names = displayName.split(/\s+/).filter(Boolean);
    const initials =
      names.length > 1
        ? `${names[0].charAt(0)}${names[names.length - 1].charAt(0)}`
        : names[0].charAt(0);
    return initials.toUpperCase();
  }

  const email = resolveAvatarEmail(userOrUsers);
  if (email) return email.charAt(0).toUpperCase();

  return "U";
};
